import { useEffect, useMemo, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useLoaderData, useNavigate, useRevalidator } from "react-router-dom";
import { ColDef, GridReadyEvent, PaginationChangedEvent, RowClickedEvent } from "ag-grid-community";
import { DataGrid } from "./Controls/DataGrid";
import { EnvelopePaper, PencilSquare, Trash } from "react-bootstrap-icons";
import { PageBar, ToolButton } from "./Controls/PageBar";
import { Staff } from "./Data/ApiTypes";
import { toLocalDate } from "./Data/Date";
import OperatorStaffModal from "./OperatorStaffModal";
import { ServerTransferMonitorStaff, StaffApi } from "./shared/api/axios-client";
import { useAtom } from "jotai";
import { authAtom } from "./Data/Atoms";
import { isAdminRole } from "./helpers/RoleManager";
import { createApiService } from "./shared/apiConfig";
import { toast } from "react-toastify";
import { CSSTransition } from "react-transition-group";
import { ConfirmModal } from "./Controls/ConfirmModal";


export function OperatorStaffListPage() {
    const nav = useNavigate();
    const staff = useLoaderData() as Staff[];
    const [showInvite, setShowInvite] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [staffToEdit, setStaffToEdit] = useState<ServerTransferMonitorStaff>();
    const [auth, setAuth] = useAtom(authAtom);
    const [staffList, setStaffList] = useState<ServerTransferMonitorStaff[]>([]);
    const [showContent, setShowContent] = useState(false);
    const [pageSize, setPageSize] = useState(1000);
    const [pageNumber, setPageNumber] = useState(1);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [staffToDelete, setStaffToDelete] = useState<ServerTransferMonitorStaff | null>(null);
    const staffService = createApiService(StaffApi);
    let isRowClick = false

    const regularColumns: ColDef<ServerTransferMonitorStaff>[] = [

        { field: "staffId", headerName: "#", type: "rightAligned", width: 100 },
        { field: "name", headerName: "Name" },
        { field: "emailAddress", headerName: "Email" },
        // { field: "note", headerName: "Note" },
        { field: "phoneNumber", headerName: "Phone", valueFormatter: x => x.value || "", width: 200 },
        { field: "roles", headerName: "Role", width: 200 },
        { field: "createdWhen", headerName: "Created", valueFormatter: x => x.value ? (toLocalDate(x.value)) : "", width: 150 }   
    ];
    const editColumns: ColDef<ServerTransferMonitorStaff>[] = [
        {
            headerName: "",
            cellRenderer: ({ data }: { data: ServerTransferMonitorStaff }) => (
                <div className="d-flex gap-1">
                    <button
                        className="btn btn-sm"
                        title="Edit Staff"
                        onClick={(e) => {
                            e.stopPropagation();
                            editButtonClick(data);
                        }}
                    >
                        <PencilSquare />
                    </button>
                    <button
                        className="btn btn-sm"
                        title="Delete Staff"
                        onClick={(e) => {
                            e.stopPropagation();
                            deleteButtonClick(data);
                        }}
                    >
                        <Trash />
                    </button>
                </div>
            ),
            filter: false,
            sortable: false,
            resizable: false,
            width: 120,
        },
    ];
    const isAdmin = isAdminRole(auth.account?.roles ?? []);
    const columns = useMemo<ColDef<ServerTransferMonitorStaff>[]>(() => {
        return isAdmin ? [...regularColumns, ...editColumns] : regularColumns;
    }, []);

    const onGridReady = (params: GridReadyEvent) => {
        params.api.sizeColumnsToFit();

    };

    const editButtonClick = (data: ServerTransferMonitorStaff): void => {
        setStaffToEdit(data);
        setShowEdit(true);
    }

    const deleteButtonClick = (data: ServerTransferMonitorStaff): void => {
        setStaffToDelete(data);
        setConfirmDelete(true);
    }

    const confirmDeleteStaff = async (): Promise<void> => {
        if (!staffToDelete) return;
        
        try {
            await staffService.deleteStaff({
                id: staffToDelete.staffId
            });
            toast.success("Staff member deleted successfully");
            fetchStaffList();
            setConfirmDelete(false);
            setStaffToDelete(null);
        } catch (error) {
            toast.error("Failed to delete staff member");
            console.error("Delete staff error:", error);
        }
    }
    const onSaveStaff = async (data: ServerTransferMonitorStaff) => {
        try {
            await staffService.updateStaff({
                id: data.staffId,
                serverRequestsPatchStaffRequest: {
                    staffId: data.staffId,
                    name: data.name,
                    emailAddress: data.emailAddress,
                    phoneNumber: data.phoneNumber,
                    role: data.role,
                    note: data.note,
                    roles: data.roles,
                    permissions: (data as any).permissions,
                    facilities: data.facilities?.map(c => c.facilityId) as any[]
                }
            });
            toast.success("Staff updated successfully");
            fetchStaffList();
        } catch (error) {
            toast.error("Failed to update staff member");
            console.error("Update staff error:", error);
            throw error;
        }
    };

    const onSaveStaffInvite = async (data: ServerTransferMonitorStaff) => {
        try {
            const response = await staffService.createStaff({
                serverRequestsCreateStaffRequest: {
                    name: data.name,
                    emailAddress: data.emailAddress,
                    phoneNumber: data.phoneNumber,
                    role: data.roles?.[0],
                    note: data.note,
                    operatorId: auth.account?.operatorId,
                    facilities: data.facilities?.map(c => c.facilityId) as any[]
                }
            });
            toast.success("Staff invited successfully");
            fetchStaffList();
            return response.data.item;
        } catch (error) {
            toast.error("Failed to invite staff member");
            console.error("Invite staff error:", error);
            throw error;
        }
    };

    const openEditPermissionsAfterInvite = (staff: ServerTransferMonitorStaff) => {
        setStaffToEdit(staff);
        setShowEdit(true);
    };


    const fetchStaffList = async () => {
        const response = await staffService.listStaff({
            pageSize: pageSize,
            pageNumber: pageNumber,
        })
        setStaffList(response.data.items || []);
    };

    useEffect(() => {
        fetchStaffList();
        setShowContent(true);
    }, []);

    return (
        <CSSTransition
            in={showContent}
            timeout={300}
            classNames="page-slide"
            unmountOnExit
        >
            <Container fluid className="h-100">
                <ConfirmModal
                    title="Delete Staff Member?"
                    message={`Are you sure you want to delete ${staffToDelete?.name}? This action cannot be undone.`}
                    cancelText="Cancel"
                    confirmText="Delete"
                    show={confirmDelete}
                    setShow={setConfirmDelete}
                    onConfirm={confirmDeleteStaff}
                />
                <OperatorStaffModal
                    show={showInvite}
                    setShow={setShowInvite}
                    onSave={onSaveStaffInvite}
                    onEditPermissionsAfterInvite={openEditPermissionsAfterInvite}
                    mode="invite"
                />
                <OperatorStaffModal
                    show={showEdit}
                    setShow={setShowEdit}
                    onSave={onSaveStaff}
                    initialData={staffToEdit}
                    mode="edit"
                />
                <Row className="h-100">
                    <Col className="h-100" style={{ display: "grid", gridTemplateColumns: "1fr", gridTemplateRows: "max-content 1fr 5px" }}>
                        <PageBar title="Staff">
                            <ToolButton onClick={() => setShowInvite(true)}>
                                <EnvelopePaper color="maroon" /> Invite Staff
                            </ToolButton>
                        </PageBar>
                        <div style={{ height: "100%", width: "100%" }}>
                            <DataGrid
                                rowData={staffList}
                                columnDefs={columns}
                                onGridReady={onGridReady}

                            />
                        </div>
                    </Col>
                </Row>
            </Container>
        </CSSTransition>
    );
}

